import { useCallback, useEffect, useMemo, useState } from "react"

import { useTransportInfiniteQuery } from "@/transport"

import type { TransportOperationResponse } from "@/types_api/backend/transport/operations"

import { getNextKeysetRequest } from "./useRunsTableTransport"

type RunsPage = TransportOperationResponse<"listRunsKeyset">

const DAY_MS = 24 * 60 * 60 * 1000
const RECENT_USAGE_WINDOW_DAYS = 30
/** Mirrors the backend's `RunsKeysetRequest.limit` cap. */
const RECENT_USAGE_PAGE_SIZE = 100
const RECENT_USAGE_MAX_PAGES = 3
const RECENT_USAGE_STALE_TIME_MS = 5 * 60 * 1000
/** Caps a boundary timer below the browser's 32-bit `setTimeout` limit. */
const MAX_BOUNDARY_TIMER_MS = DAY_MS

const RECENTLY_USED_WINDOWS = [
  { value: "1d", label: "Past 24 hours", days: 1 },
  { value: "7d", label: "Past 7 days", days: 7 },
  { value: "30d", label: "Past 30 days", days: RECENT_USAGE_WINDOW_DAYS },
] as const

export interface RecentResourceUsage {
  agents: ReadonlyMap<string, number>
  metrics: ReadonlyMap<string, number>
  testSets: ReadonlyMap<string, number>
  personas: ReadonlyMap<string, number>
  /** Keyed by template display name, the only template reference runs carry. */
  templates: ReadonlyMap<string, number>
  /** True when older runs inside the window were not scanned. */
  isTruncated: boolean
  isLoading: boolean
  /** True when any page of run history failed, so the usage is incomplete. */
  isError: boolean
  retry: () => void
  /** Time the windows are measured from; advances as usage crosses a window edge. */
  now: number
}

function recordLatest(
  usage: Map<string, number>,
  id: string | null | undefined,
  at: number,
) {
  if (id && at > (usage.get(id) ?? -Infinity)) {
    usage.set(id, at)
  }
}

export function collectRecentResourceUsage(pages: readonly RunsPage[]) {
  const agents = new Map<string, number>()
  const metrics = new Map<string, number>()
  const testSets = new Map<string, number>()
  const personas = new Map<string, number>()
  const templates = new Map<string, number>()
  for (const page of pages) {
    for (const run of page.items) {
      const at = run.created_at ? new Date(run.created_at).getTime() : NaN
      if (!Number.isFinite(at)) {
        continue
      }
      recordLatest(agents, run.agent_id, at)
      recordLatest(testSets, run.test_set_id, at)
      recordLatest(personas, run.persona_id, at)
      recordLatest(templates, run.template_name, at)
      run.metric_ids?.forEach((metricId) => recordLatest(metrics, metricId, at))
    }
  }
  return { agents, metrics, testSets, personas, templates }
}

/** Earliest time after `now` at which a last-used time leaves a window. */
function nextWindowBoundary(
  usage: ReturnType<typeof collectRecentResourceUsage>,
  now: number,
) {
  let next = Infinity
  for (const lastUsed of Object.values(usage)) {
    for (const at of lastUsed.values()) {
      for (const { days } of RECENTLY_USED_WINDOWS) {
        const edge = at + days * DAY_MS
        if (edge > now && edge < next) {
          next = edge
        }
      }
    }
  }
  return Number.isFinite(next) ? next : null
}

/**
 * Last-used time per agent, metric, test set, persona, and template, derived
 * from the most recent runs in the current workspace.
 */
export function useRecentResourceUsage(): RecentResourceUsage {
  const [windowStart] = useState(() =>
    new Date(Date.now() - RECENT_USAGE_WINDOW_DAYS * DAY_MS).toISOString(),
  )
  const query = useTransportInfiniteQuery({
    operation: "listRunsKeyset",
    request: {
      limit: RECENT_USAGE_PAGE_SIZE,
      sort_column: "createdAt",
      sort_direction: "DESC",
      filters: { date_range: { start: windowStart } },
    },
    getNextRequest: (lastPage, _allPages, previousRequest) =>
      getNextKeysetRequest(lastPage, previousRequest),
    options: {
      abortOnUnmount: true,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: RECENT_USAGE_STALE_TIME_MS,
    },
  })
  const { data, hasNextPage, isFetchingNextPage, fetchNextPage, isError } =
    query
  const { refetch } = query
  const pageCount = data?.pages.length ?? 0
  const canFetchMore = hasNextPage && pageCount < RECENT_USAGE_MAX_PAGES

  useEffect(() => {
    if (canFetchMore && !isFetchingNextPage && !isError) {
      void fetchNextPage()
    }
  }, [canFetchMore, isFetchingNextPage, fetchNextPage, isError])
  const retry = useCallback(() => {
    void refetch()
  }, [refetch])

  const usage = useMemo(
    () => collectRecentResourceUsage(data?.pages ?? []),
    [data],
  )
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const boundary = nextWindowBoundary(usage, now)
    if (boundary === null) {
      return
    }
    const timeoutId = window.setTimeout(
      () => setNow(Date.now()),
      Math.min(boundary - Date.now() + 1, MAX_BOUNDARY_TIMER_MS),
    )
    return () => window.clearTimeout(timeoutId)
  }, [usage, now])

  return {
    ...usage,
    isTruncated: Boolean(hasNextPage) && !canFetchMore,
    isLoading: !isError && (query.isLoading || canFetchMore),
    isError,
    retry,
    now,
  }
}

/** Collection filter field that matches rows used within the chosen window. */
export function recentlyUsedFilterField<T>(
  usage: RecentResourceUsage,
  resource: "agents" | "metrics" | "testSets" | "personas" | "templates",
  getId: (row: T) => string,
) {
  const lastUsed = usage[resource]
  return {
    id: "recently-used",
    label: usage.isTruncated
      ? `Recently Used (latest ${RECENT_USAGE_PAGE_SIZE * RECENT_USAGE_MAX_PAGES} runs)`
      : "Recently Used",
    kind: "select" as const,
    preserveOptionOrder: true,
    single: true,
    unavailable: usage.isError
      ? {
          state: "error" as const,
          message: "Couldn't load recent runs.",
          onRetry: usage.retry,
        }
      : usage.isLoading
        ? { state: "loading" as const, message: "Loading recent runs…" }
        : undefined,
    options: RECENTLY_USED_WINDOWS.map(({ value, label }) => ({
      value,
      label,
    })),
    value: (row: T) => {
      const lastUsedAt = lastUsed.get(getId(row))
      if (lastUsedAt === undefined) {
        return []
      }
      const age = usage.now - lastUsedAt
      return RECENTLY_USED_WINDOWS.filter(
        ({ days }) => age <= days * DAY_MS,
      ).map(({ value }) => value)
    },
  }
}
